import { useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import BedbankMarginText from 'components/OfferPage/Bedbank/BedbankMargin'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import OfferListEventsContext, {
  OfferListEvents,
} from 'components/OfferList/OfferListEventsContext'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import Group from 'components/utils/Group'
import { offerIncludesPayAtProperty } from 'components/OfferPage/utils'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import { buildSuggestedDatesString } from 'lib/search/searchUtils'
import { useMetaSearchMobileV2Enabled } from 'components/Pages/HotelSearchPage/useMetaSearchMobileV2'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  offerPrice: number
  isFlexibleSearch?: string
  suggestedDates?: {
    checkIn: string
    checkOut: string
  }
  nightsToDisplay: number
  promoteAsBundle: boolean
  saleUnit: string
  rooms: Array<App.Occupants>
  discountPercentage: number
  value: number
  hotelNights: number
  offerLocations: Array<string>
  displayMargin?: boolean
  margin?: number
  marginAud?: number
  supplier?: string
  directSearchPrices?: App.OfferListMetadataPricing
  shownRate?: {
    mobilePromotion?: boolean
  }
  bundlePropertyFees?: number
  propertyFees?: number
  bundleTaxesAndFees?: number
  taxesAndFees?: number
  bundlePrice: number
  hotelPrice: number
  postTaxDiscount?: number
  flightPrice?: number
  offerUrl: string
  hidePrice?: boolean
  hideIncludesTaxesAndFees?: boolean
}

function BedbankMetaSearchTileGarden({
  offer,
  offerPrice,
  isFlexibleSearch,
  suggestedDates,
  nightsToDisplay,
  promoteAsBundle,
  saleUnit,
  rooms,
  hotelNights,
  offerLocations,
  displayMargin,
  margin,
  marginAud,
  supplier,
  directSearchPrices,
  shownRate,
  bundlePropertyFees,
  propertyFees,
  bundleTaxesAndFees,
  taxesAndFees,
  bundlePrice,
  hotelPrice,
  postTaxDiscount,
  flightPrice,
  offerUrl,
  hidePrice = false,
  hideIncludesTaxesAndFees = false,
}: Props) {
  const intl = useIntl()
  const isMetaSearchMobileV2 = useMetaSearchMobileV2Enabled()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  useEffect(() => {
    dispatchOfferListEvent({
      type: OfferListEvents.pricingCalculated,
      offerId: offer.id,
      availability: true,
      roomRateId: undefined,
      price: hidePrice ? undefined : offerPrice,
      memberPrice: undefined,
      duration: nightsToDisplay,
    })
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const priceCaptionText = useMemo(() => {
    const datesOrNights = isFlexibleSearch && suggestedDates
      ? buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut)
      : intl.formatMessage(
          {
            id: '6AUOMDEYYfYwnolGg0v0',
            defaultMessage: '{nights, plural, one {# night} other {# nights}}',
            description: 'Night count for bedbank meta search tile price caption',
          },
          { nights: nightsToDisplay },
        )
    if (promoteAsBundle) {
      return intl.formatMessage(
        {
          id: 'qJHWCO8ebCcPp3yfyAwA',
          defaultMessage: '{datesOrNights} & flights from',
          description: 'Price caption with nights/dates and flights for bedbank meta search tile',
        },
        { datesOrNights },
      )
    }
    return intl.formatMessage(
      {
        id: '_jxVeaZsu0exNT2LrThk',
        defaultMessage: '{datesOrNights} from',
        description: 'Price caption with nights/dates for bedbank meta search tile',
      },
      { datesOrNights },
    )
  }, [isFlexibleSearch, suggestedDates, nightsToDisplay, promoteAsBundle, intl])

  const _propertyFees = promoteAsBundle ? bundlePropertyFees : propertyFees
  const _hotelPrice = promoteAsBundle ? bundlePrice : hotelPrice

  if (hidePrice) {
    return null
  }

  return (
    <>
      {!!offerPrice && <>
        {!isMetaSearchMobileV2 && <Group direction="vertical" gap={8} horizontalAlign="start">
          {offerIncludesPayAtProperty(offer) && (
            <PayAtPropertyPill />
          )}
        </Group>}
        <PriceRowPriceCaption>
          {priceCaptionText}
        </PriceRowPriceCaption>
        <LuxPlusPriceStack
          size="L"
          price={offerPrice}
          saleUnit={saleUnit}
        />
        {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
      </>}

      <PriceRowAgentHubCommission
        productType={offer.productType}
        offerId={offer.id}
        offerLocations={offerLocations}
      />

      {displayMargin && (
        <BedbankMarginText
          margin={margin!}
          marginAud={marginAud!}
          supplier={supplier!}
          type="bedbank_estimate"
        />
      )}
      {(directSearchPrices || (shownRate && hotelNights)) && (
        <OfferPriceDetailsRow
          trigger="price-row"
          triggerSize="M"
          offer={offer}
          rooms={rooms}
          propertyFees={_propertyFees}
          taxesAndFees={promoteAsBundle ? bundleTaxesAndFees : taxesAndFees}
          hotelPrice={_hotelPrice - (_propertyFees ?? 0)}
          postTaxDiscount={promoteAsBundle ? undefined : postTaxDiscount}
          flightPrice={promoteAsBundle ? flightPrice : undefined}
          duration={hotelNights}
          cta={
            <TextButton kind="primary" fit="flex" to={offerUrl}>
              {intl.formatMessage({
                id: 'hljtoVQgozf8uZlAFqhn',
                defaultMessage: 'View Offer',
                description: 'CTA button in meta search price details row to view the offer',
              })}
            </TextButton>
          }
          context="search"
          hasMobilePromotion={shownRate?.mobilePromotion}
          hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
        />
      )}
    </>
  )
}

export default BedbankMetaSearchTileGarden
